type WindowWithPostRaf = Window & {
  requestPostAnimationFrame?: (callback: FrameRequestCallback) => number
}

/**
 * Estimates input-to-present latency: one rAF to align with the frame loop, then
 * `requestPostAnimationFrame` when available (after compositor paint), else a second rAF.
 */
export function measureInputToPresentLatency(onComplete: (latencyMs: number) => void) {
  const start = performance.now()

  const finish = () => {
    onComplete(performance.now() - start)
  }

  const win = window as WindowWithPostRaf
  if (typeof win.requestPostAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      win.requestPostAnimationFrame!(finish)
    })
    return
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(finish)
  })
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true
  }

  return target.isContentEditable
}

export function shouldTrackInputLatencyEvent(event: PointerEvent | KeyboardEvent): boolean {
  if (isTextEditingTarget(event.target)) {
    return false
  }

  if (event.type === 'keydown') {
    const keyEvent = event as KeyboardEvent
    if (keyEvent.repeat) {
      return false
    }
  }

  if (event.type === 'pointerdown') {
    const pointerEvent = event as PointerEvent
    if (pointerEvent.button !== 0) {
      return false
    }
  }

  return true
}
